import { RoleEnum } from "./invitation.types";

export interface ProfileType {
  id?: number;
  profile: string;
  url_slug?: string;
  is_default?: boolean;
  name: RoleEnum;
  attributes: AttributesType;
  access: ProfileAccessType;
  inclusion_criteria: InclusionType;
  consent: ConsentType;
  site: SiteType;
  // Deterministic profiling: precision the birthdate is collected at (top-level, not in attributes).
  birthdate_precision?: 'YEAR' | 'MONTH_YEAR' | 'DAY_MONTH_YEAR' | null;
}

export enum ProfileFieldCollectionStateEnum {
  HIDDEN = 'HIDDEN',
  OPTIONAL = 'OPTIONAL',
  REQUIRED = 'REQUIRED'
}

export type ProfileFieldKeyType =
  | 'nickname'
  | 'email'
  | 'phonenumber'
  | 'external_id'
  | 'firstname'
  | 'lastname'
  | 'birthdate'
  | 'address';

// Precision a birthdate is collected at (maps to the date-picker DateFormatEnum DAY/MONTH/YEAR).
export type BirthdatePrecisionType = 'YEAR' | 'MONTH_YEAR' | 'DAY_MONTH_YEAR';

export type ProfileAccessSignInModeType =
  | IdentificationMethodEnum.PASSWORD_SIGNIN
  | IdentificationMethodEnum.NO_SIGNIN
  | IdentificationMethodEnum.SECRET_QUESTIONS;

export enum ProfileAccessConstraintTypeEnum {
  AT_LEAST_ONE_OF = 'AT_LEAST_ONE_OF'
}

export interface ProfileAccessFieldType {
  key: ProfileFieldKeyType;
  collectionState: ProfileFieldCollectionStateEnum;
  identifierEnabled: boolean;
  displayOrder: number;
  // Per-field extra config (today only birthdate uses it: its collection precision).
  config?: { precision?: BirthdatePrecisionType } | null;
}

export interface ProfileAccessConstraintType {
  type: ProfileAccessConstraintTypeEnum;
  fieldKeys: ProfileFieldKeyType[];
}

export interface ProfileAccessType {
  signInMode: ProfileAccessSignInModeType;
  fields: ProfileAccessFieldType[];
  constraints: ProfileAccessConstraintType[];
  secretQuestions: SecretQuestionType[];
}

export interface SiteType {
  question_site: string;
  question_subsite: string;
}

export interface ConsentType {
  id: number;
  has_consent: boolean;
  type: ProfileConsentEnum;
}

export enum ProfileConsentEnum {
  NONE = '',
  SIGNATURE = 'SIGNATURE',
  CHECKBOX = 'CHECKBOX'
}

export interface SecretQuestionType {
  id?: number;
  question: string;
  type: SecretQuestionTypeEnum;
  order?: number;
}

export enum SecretQuestionTypeEnum {
  DATE = 'DATE',
  NUMBER = 'NUMBER',
  TEXT = 'TEXT'
}

export interface InclusionType {
  has_inclusion_questions: boolean;
  inclusion_questions: InclusionQuestionType[];
}

export interface InclusionQuestionType {
  id?: number;
  question: string;
  eligible_answer: boolean;
}

export enum IdentificationMethodEnum {
  NONE = '',
  PASSWORD_SIGNIN = 'PASSWORD-SIGNIN',
  EMAIL = 'LOGIN-PASSWORD',
  FILE_NUMBER = 'FILE_NUMBER',
  IDENTIFICATION_OFF = 'IDENTIFICATION_OFF',
  NO_SIGNIN = 'NO-SIGNIN',
  SECRET_QUESTIONS = 'SECRET-QUESTIONS',
  WITHOUT_IDENTIFICATION = 'WITHOUT_IDENTIFICATION',
  NICKNAME_PASSWORD= 'NICKNAME-PASSWORD'
}

export const isPasswordSignInMethod = (
  method?: IdentificationMethodEnum | string | null
): boolean => (
  method === IdentificationMethodEnum.NONE
  || method === IdentificationMethodEnum.PASSWORD_SIGNIN
  || method === IdentificationMethodEnum.EMAIL
  || method === IdentificationMethodEnum.NICKNAME_PASSWORD
)

export const isNoSignInMethod = (
  method?: IdentificationMethodEnum | string | null
): boolean => (
  method === IdentificationMethodEnum.NO_SIGNIN
  || method === IdentificationMethodEnum.WITHOUT_IDENTIFICATION
)

export enum AttributeEnum {
  FIRSTNAME = 'has_firstname',
  LASTNAME = 'has_lastname',
  EMAIL = 'has_email',
  PHONENUMBER = 'has_phonenumber',
  EXTERNALID = 'has_external_id',
  VOCALISATION = 'is_vocalisation_enabled',
  NICKNAME = 'has_nickname',
  SITES = 'is_site_requested',
  SPEECH = 'is_speech_recognition_enabled',
  DIRECT_INCLUSION = 'is_direct_inclusion_authorized',
  STRATIFICATION = 'has_stratification',
  // Deterministic profiling: the role collects list attributes (its stratification questions) and/or a birthdate.
  PROFILE_ATTRIBUTES = 'has_profile_attributes',
  BIRTHDATE = 'has_birthdate',
  CAN_SIGN_UP_ONLY_WITH_INVITATION = 'can_sign_up_only_with_invitation',
  // Subjects only: false ⇒ data-only role (pre-created, never signs up/logs in).
  IS_AUTHENTICATIVE = 'is_authenticative'
}

// Subjects only: NIR collection at signup (SNDS appariement — the parent
// self-declares their OWN numéro de sécurité sociale on a dedicated step).
// Absent/unknown ⇒ HIDDEN (fail-safe: stale caches degrade to "no NIR step").
export type NirCollectionModeType = 'HIDDEN' | 'OPTIONAL' | 'REQUIRED';

export type AttributesType = Record<AttributeEnum, boolean> & {
  // Per-role signup contact verification. true/undefined ⇒ the participant must confirm a
  // 6-digit code for that channel before finishing signup; false ⇒ the role skips it.
  is_email_verification_required?: boolean;
  is_phone_verification_required?: boolean;
  nir_collection_mode?: NirCollectionModeType;
  // The appariement also links the NIR HOLDER's own care (e.g. the mother's) → the NIR
  // step also asks the holder's date de naissance. Only meaningful with a NIR step.
  nir_link_holder_care?: boolean;
  // Family composition (DATA-ONLY roles, e.g. Enfant): when enabled on such a role, the
  // first registering family member declares how many members exist (min..max, wording
  // researcher-configured) and the rows are auto-created at consent.
  auto_create_enabled?: boolean;
  auto_create_min?: number | null;
  auto_create_max?: number | null;
  auto_create_question_label?: string | null;
  // Absence declaration (« pas de second parent »): this SUBJECT role can be declared
  // absent from the dossier by a registrant of the role `absence_declarable_by_study_role_id`
  // (one Oui/Non radio on the family-composition step; the label is per-lang resolved by
  // the API with a primary-language fallback, like auto_create_question_label).
  absence_declarable_by_study_role_id?: number | null;
  absence_question_label?: string | null;
  // Which answer of that question declares the absence: false/absent = « Non » (a question
  // like « Y a-t-il un second titulaire ? »), true = « Oui » (« Je suis la seule titulaire »).
  absence_declared_on_yes?: boolean;
};

export interface IdentificationOptionType {
  label: string;
  value: IdentificationMethodEnum;
  attributeNeeded?: AttributeEnum;
}
